import Vue from 'vue';
import { mapActions, mapGetters } from 'vuex';
import { Component, Prop, Watch } from 'vue-property-decorator';

import {
    BirthdayEmployeeInfo,
    CalendarEventDetail,
    CalendarEventResponseData,
    CalendarFolderInfo,
    VacationerInfo,
} from './types';
import { CURRENT } from '@account/store/types';
import * as types from '@webparts/store/types';

import * as dateutil from '@appbase/util/date-helper';
import menuHelper from '@appbase/util/menu-helper';
import { IEDate, uGetDateFormat } from '@appbase/util/date-helper';

import calendarApi from '@webparts/components/webparts/YASKAWA/Calendar/api'

import EventListItem from './EventListItem.vue';
import { LoadingBar } from '@appbase/components/loading';
import NoData from '@webparts/components/common/NoData.vue';
import BirthdayItem from './BirthdayItem.vue';
import VacationerItem from './VacationerItem.vue';

export const COMMON_AUTO_COLOR_ARRAY = [
    '#ff4b09',
    '#ffc609',
    '#e0ff03',
    '#bdff09',
    '#29e1bf',
    '#0942ff',
    '#241eb4',
    '#c609ff',
    '#ff119a',
    '#c9dbdb',
];

@Component({
    components: {
        EventListItem,
        LoadingBar,
        NoData,
        BirthdayItem,
        VacationerItem,
    },
    computed: {
        ...mapGetters({
            current: CURRENT,
            folders: types.WEBPART_CALENDARFOLDER_DATA,
            dailyEvents: types.WEBPART_CALENDARDAILYEVENT_DATA,
        }),
    },
    methods: mapActions({
        loadEvents: types.WEBPART_CALENDARDAILYEVENT_DATA,
    }),
})
export default class EventList extends Vue {
    //
    // ────────────────────────────────────────────────── I ──────────
    //   :::::: P R O P S : :  :   :    :     :        :          :
    // ────────────────────────────────────────────────────────────
    //
    @Prop({ type: String }) value!: string;
    @Prop({ type: Array }) weeks!: string[];
    @Prop({ type: Boolean, default: false }) preview!: boolean;

    //
    // ────────────────────────────────────────────────────── I ──────────
    //   :::::: A C T I O N S : :  :   :    :     :        :          :
    // ────────────────────────────────────────────────────────────────
    //
    loadEvents!: (payload: { start: Date; end: Date }) => Promise<CalendarEventResponseData[]>;

    //
    // ────────────────────────────────────────────────────── I ──────────
    //   :::::: G E T T E R S : :  :   :    :     :        :          :
    // ────────────────────────────────────────────────────────────────
    //
    current!: account.TCurrent;
    folders!: CalendarFolderInfo[];
    dailyEvents!: CalendarEventResponseData[];
    //
    // ──────────────────────────────────────────────── I ──────────
    //   :::::: D A T A : :  :   :    :     :        :          :
    // ──────────────────────────────────────────────────────────
    //
    events: CalendarEventDetail[] = [];
    loading: boolean = true;
    text: string = this.$t('등록된_일정이_없습니다').toString();
    today: boolean = false;
    noMailBox: boolean = false;

    currentTab: string = 'calendar';

    //
    // ──────────────────────────────────────────────────────── I ──────────
    //   :::::: C O M P U T E D : :  :   :    :     :        :          :
    // ──────────────────────────────────────────────────────────────────
    //
    get MonthDayDateFormat() {
        const szDate = uGetDateFormat('MM.DD', this.date);
        const szWeek = this.weeks[this.date.getDay()];
        return `${szDate} <span>${szWeek}</span>`;
    }

    get YearMonthDayDateFormat() {
        const szDate = uGetDateFormat('yyyy.MM.DD', this.date);
        const szWeek = this.weeks[this.date.getDay()];
        return `${szDate} (${szWeek})`;
    }

    get YearMonthDateFormat() {
        return uGetDateFormat('yyyy.MM', this.date);
    }

    get date(): Date {
        return IEDate(this.value);
    }

    //
    // ────────────────────────────────────────────────────── I ──────────
    //   :::::: W A T C H E S : :  :   :    :     :        :          :
    // ────────────────────────────────────────────────────────────────
    //
    @Watch('value')
    onValueChanged(newVal: string) {
        this.today = uGetDateFormat('yyyy-MM-DD', new Date()) == newVal;
        const dateStart = IEDate(this.value);
        const dateEnd = IEDate(this.value);
        dateEnd.setDate(dateEnd.getDate() + 1);

        this.loading = true;
        this.loadEvents({ start: dateStart, end: dateEnd });
    }

    @Watch('dailyEvents')
    onEventsChanged(newVal: CalendarEventResponseData[]) {
        if (this.currentTab == 'calendar') {
            this.events = newVal.map<CalendarEventDetail>(e => ({
                scheduleId: e.id,
                isAllDay: e.isAllDayEvent,
                title: e.subject,
                place: e.location!,
                startDate: dateutil.IEDate(e.start),
                endDate: dateutil.IEDate(e.end),
                color: this.findColor(e.parentFolderId),
            }));
        }

        this.loading = false;
    }

    @Watch('currentTab')
    onTabChanged() {
        switch (this.currentTab) {
            case 'calendar':
                this.onEventsChanged(this.dailyEvents);
                break;
        }
    }

    //
    // ────────────────────────────────────────────────────── I ──────────
    //   :::::: M E T H O D S : :  :   :    :     :        :          :
    // ────────────────────────────────────────────────────────────────
    //
    handleSetToday = () => this.$emit('input', dateutil.uGetDateFormat('yyyy-MM-DD', new Date()));

    findColor(folderId: string) {
        return this.folders.find(f => f.id === folderId)?.color;
    }

    goToCalendar() {
        if (this.preview) return;
        let origin = window.location.origin;
        let menuId = this.portalCache.menu.items
            .filter((i: appbase.cache.TMenuItem) => i.baseUrl == '/Mail')[0]
            .children.filter((i: appbase.cache.TMenuItem) => i.targetUrl && i.frameUrl == '/calendar')[0].menuId;
        let menu = menuHelper.findOne(menuId);
        if (menu) menuHelper.next(menu);
    }

    openCalendar(){
        let url = 'https://outlook.office.com/calendar';
        window.open(url, '_blank');
    }

    //
    // ────────────────────────────────────────────────────────── I ──────────
    //   :::::: L I F E S T Y L E : :  :   :    :     :        :          :
    // ────────────────────────────────────────────────────────────────────
    //
    created() {
        this.today = dateutil.uGetDateFormat('yyyy-MM-DD', new Date()) == this.value;
        this.onEventsChanged(this.dailyEvents);
    }
}
